/**
 * SAT c_FormaPago — the form of payment a CFDI 4.0 states (Anexo 20, catálogo
 * c_FormaPago). Shared by the API (validation, emission) and the web (the
 * customer's "Tipo de Pago" select), so both read the same list.
 */
export const SAT_PAYMENT_FORM_CODES = [
  '01', // Efectivo
  '02', // Cheque nominativo
  '03', // Transferencia electrónica de fondos
  '04', // Tarjeta de crédito
  '05', // Monedero electrónico
  '06', // Dinero electrónico
  '08', // Vales de despensa
  '12', // Dación en pago
  '13', // Pago por subrogación
  '14', // Pago por consignación
  '15', // Condonación
  '17', // Compensación
  '23', // Novación
  '24', // Confusión
  '25', // Remisión de deuda
  '26', // Prescripción o caducidad
  '27', // A satisfacción del acreedor
  '28', // Tarjeta de débito
  '29', // Tarjeta de servicios
  '30', // Aplicación de anticipos
  '31', // Intermediario pagos
  '99', // Por definir
] as const;

export type SatPaymentFormCode = (typeof SAT_PAYMENT_FORM_CODES)[number];

/** Transferencia electrónica de fondos — what a CFDI states when nothing else is recorded. */
export const DEFAULT_SAT_PAYMENT_FORM_CODE: SatPaymentFormCode = '03';

/** "Por definir": the only form a PPD CFDI may state. */
export const SAT_PAYMENT_FORM_TO_BE_DEFINED: SatPaymentFormCode = '99';

const SAT_PAYMENT_FORM_SET: ReadonlySet<string> = new Set(SAT_PAYMENT_FORM_CODES);

export function isSatPaymentFormCode(value: unknown): value is SatPaymentFormCode {
  return typeof value === 'string' && SAT_PAYMENT_FORM_SET.has(value);
}

/** A FormaPago the SAT would reject for this document — never sent to the provider. */
export class CfdiPaymentFormError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CfdiPaymentFormError';
  }
}

/**
 * The FormaPago a CFDI states.
 *
 * Precedence: the code the call sends (`requested`) > the code recorded on the
 * customer (`recorded`) > '03'. The law then decides over all of them (CFDI 4.0
 * Anexo 20, Guía de llenado del Comprobante, campo FormaPago):
 * - MetodoPago PPD: the payment has not happened, so the form is unknown and
 *   the document MUST state '99' (Por definir) — the real form goes on the
 *   Complemento de Pago. Whatever the call or the customer says is replaced.
 * - MetodoPago PUE: the payment happened at issuance, so '99' is not allowed;
 *   the document must state the actual form. Refused, never guessed.
 */
export function resolveCfdiPaymentForm(input: {
  requested?: string | null;
  recorded?: string | null;
  paymentMethodCode?: 'PUE' | 'PPD';
}): SatPaymentFormCode {
  if ((input.paymentMethodCode ?? 'PUE') === 'PPD') return SAT_PAYMENT_FORM_TO_BE_DEFINED;
  const chosen = input.requested?.trim() || input.recorded?.trim() || DEFAULT_SAT_PAYMENT_FORM_CODE;
  if (!isSatPaymentFormCode(chosen)) {
    throw new CfdiPaymentFormError(
      `FormaPago "${chosen}" is not a SAT c_FormaPago code (${SAT_PAYMENT_FORM_CODES.join(', ')}).`,
    );
  }
  if (chosen === SAT_PAYMENT_FORM_TO_BE_DEFINED) {
    throw new CfdiPaymentFormError(
      'FormaPago 99 (Por definir) is only allowed with MetodoPago PPD (CFDI 4.0 Anexo 20, Guía de llenado). ' +
        'A PUE CFDI must state how it was paid — record the payment form on the customer or send paymentFormCode.',
    );
  }
  return chosen;
}
